import React, { useEffect, useRef } from 'react';

import { useRecaptcha } from '@fd/customHooks/useRecaptcha';
import Grid from '@material-ui/core/Grid';
import { Theme } from '@material-ui/core/styles';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/styles';
import { Form, Formik } from 'formik';
import { useSnackbar } from 'notistack';
import ReCAPTCHA from 'react-google-recaptcha';
import { getActiveLanguage, getTranslate, Translate } from 'react-localize-redux';
import { connect } from 'react-redux';

import { accountActions } from '../../actions/account.actions';
import { accountConstants } from '../../constants/account.constants';
import { maxLength, validate } from '../../helpers/validation';
import { accountRoutesConst as routes } from '../../routes/account.routes';
import LinkButton from '../../ui/Button/LinkButton';
import { IntroButton } from './Common/IntroButton';
import AuthLayout from './Common/Layout';
import LoginTitle from './Login/LoginTitle';

const useStyles = makeStyles((theme: Theme) => ({
  formFields: {
    // remove chrome autofill color
    '& input:-webkit-autofill': {
      boxShadow: '0 0 0 30px white inset !important',
    },
  },
  textField: {
    // prevent jumping screen
    minHeight: '68px',
    marginBottom: '0.5em',
  },
  extras: {
    [theme.breakpoints.down('sm')]: {
      alignItems: 'center',
    },
  },
  linkBtn: {
    textTransform: 'none',
  },
  recaptchaContainer: {
    marginBottom: theme.spacing(3),
  },
}));

const Title = () => (
  <Translate>
    {(translate) => (
      <LoginTitle title={translate('Forgot_your_password') as string}>
        <Typography variant="subtitle1" style={{ color: '#ffffff', letterSpacing: 'normal' }}>
          {translate('Provide_your_email_address')}
        </Typography>
      </LoginTitle>
    )}
  </Translate>
);

type ForgotPasswordErrorValues = {
  email: string;
};

const validateForgotPasswordForm = (values, translate) => {
  const { email } = values;
  const errors: Partial<ForgotPasswordErrorValues> = {};
  const emailFormatError = (validate.email(values.email) as unknown) as string;
  if (!email) {
    errors.email = translate('Required') as string;
  } else if (emailFormatError) {
    errors.email = emailFormatError;
  }
  return errors;
};

type Props = MappedState & MappedDispatch;
const ForgotPassword = (props: Props) => {
  const { translate, account, dispatchRequestPasswordReset, language } = props;
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const inputRef = useRef<HTMLInputElement>(null);
  const recaptchaRef = useRef<ReCAPTCHA>(null);

  const {
    isRecaptchaValid,
    recaptchaSiteKey,
    handleRecaptchaTest,
    handleRecaptchaExpired,
  } = useRecaptcha();

  useEffect(() => {
    inputRef?.current?.focus();
  }, []);

  return (
    <AuthLayout title={Title}>
      <Formik
        validateOnChange={false}
        validateOnBlur={false}
        isInitialValid={() => {
          return !!account?.email && validate.email(account?.Email || '') === undefined;
        }}
        validate={(values) => validateForgotPasswordForm(values, translate)}
        initialValues={{ email: account.Email || '' }}
        onSubmit={async (values, { setSubmitting, setFieldError }) => {
          try {
            await dispatchRequestPasswordReset(values.email, recaptchaRef.current?.getValue());
            setSubmitting(false);
            enqueueSnackbar(translate('Password_has_been_reset'), {
              variant: 'success',
            });
          } catch (error) {
            if (error.message === 'User_does_not_exist') {
              setFieldError('email', translate('Nobody_registered_with_that_address') as string);
              enqueueSnackbar(translate('Nobody_registered_with_that_address'), {
                variant: 'error',
              });
            } else {
              enqueueSnackbar(translate('Something_went_wrong'), {
                variant: 'error',
              });
            }
            setSubmitting(false);
          }
        }}
      >
        {({ isSubmitting, values, touched, errors, handleChange, handleBlur }) => (
          <Grid container alignItems="stretch" direction="column" wrap="nowrap" spacing={2}>
            <Grid item>
              <Form>
                <Grid container spacing={0} className={classes.formFields}>
                  <Grid item xs={12} lg={11}>
                    <TextField
                      inputRef={inputRef}
                      name="email"
                      label={translate('Email')}
                      placeholder={translate('Email') as string}
                      type="text"
                      autoComplete="email"
                      value={values.email}
                      fullWidth
                      inputProps={{
                        maxLength: maxLength.email,
                        'data-fd': 'email',
                      }}
                      className={classes.textField}
                      error={touched.email && !!errors.email}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      helperText={touched.email && errors.email}
                    />
                  </Grid>
                  <Grid item xs={12} lg={11}>
                    <Grid className={classes.recaptchaContainer} item>
                      <ReCAPTCHA
                        ref={recaptchaRef}
                        data-fd="reset-password-recaptcha"
                        hl={language}
                        sitekey={recaptchaSiteKey}
                        onChange={handleRecaptchaTest}
                        onExpired={handleRecaptchaExpired}
                      />
                    </Grid>
                  </Grid>
                  <Grid item xs={12} lg={11}>
                    <IntroButton
                      resource={accountConstants.RESET_PASSWORD}
                      loading={isSubmitting}
                      disabled={isSubmitting || !isRecaptchaValid}
                      data-fd="reset-password"
                    >
                      <Translate id={'Reset_password'} />
                    </IntroButton>
                  </Grid>
                </Grid>
              </Form>
            </Grid>

            <Grid item container direction="column" className={classes.extras}>
              <Grid item xs={12}>
                <LinkButton
                  color="primary"
                  to={routes.Login}
                  type="button"
                  disabled={isSubmitting}
                  fdKey="login-instead"
                  className={classes.linkBtn}
                >
                  <Translate id="Login_instead" />
                </LinkButton>
              </Grid>
            </Grid>
          </Grid>
        )}
      </Formik>
    </AuthLayout>
  );
};

type MappedState = ReturnType<typeof mapStateToProps>;
const mapStateToProps = (state: AppState) => {
  const { account } = state;
  return {
    account,
    translate: getTranslate(state.locale),
    language: getActiveLanguage(state.locale),
  };
};

type MappedDispatch = ReturnType<typeof mapDispatchToProps>;
const mapDispatchToProps = (dispatch: ThunkDispatch) => {
  return {
    dispatchRequestPasswordReset: (email, token) =>
      dispatch(accountActions.requestPasswordReset(email, token)),
  };
};
export default connect(mapStateToProps, mapDispatchToProps)(ForgotPassword);
